import React, { useEffect, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Radio, TrendingUp, Trophy, CreditCard, Users, Layers, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useMobile } from "./MobileApp";
import { Glass, ProLock, Rise, SectionTitle, Spinner, clp, hora } from "./ui";

const PERIODOS = [["dia", "Hoy"], ["semana", "7 días"], ["mes", "30 días"], ["anio", "12 meses"]];
const CANAL = { POS: "Caja", AUTOSERVICIO: "Autoservicio", ECOMMERCE: "Tienda online" };
const METODO = { EFECTIVO: "Efectivo", TARJETA_DEBITO: "Débito", TARJETA_CREDITO: "Crédito", TARJETA: "Tarjeta", TRANSFERENCIA: "Transferencia", MIXTO: "Mixto", WEBPAY: "Webpay", MP_POINT: "Mercado Pago" };

const Rank = ({ rows, label, value, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <Glass className="p-4 space-y-3" data-testid={testId}>
      {rows.length ? rows.map((r, i) => (
        <div key={i}>
          <div className="flex justify-between text-xs gap-2"><span className="truncate text-slate-200">{label(r)}</span><span className="font-mono text-slate-100 shrink-0">{value ? value(r) : clp(r.total)}</span></div>
          <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-cyan-300" style={{ width: `${(r.total / max) * 100}%` }} /></div>
        </div>
      )) : <p className="text-xs text-slate-500">Sin datos en el período.</p>}
    </Glass>
  );
};

const Vivo = ({ storeId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const load = () => api.get("/mobile/ventas-vivo", { params: { store_id: storeId || undefined } }).then((r) => setRows(r.data.ventas)).catch(() => {});
    load(); const t = setInterval(load, 15000); return () => clearInterval(t);
  }, [storeId]);
  if (!rows) return <Spinner />;
  return (
    <div className="space-y-2" data-testid="ventas-vivo">
      {rows.slice(0, 12).map((v, i) => (
        <Rise key={v.sale_id} i={i}>
          <Glass className={`p-3.5 flex items-center gap-3 ${v.estado === "ANULADA" ? "opacity-60" : ""}`} data-testid={`venta-vivo-${v.sale_id}`}>
            <div className="text-[10px] font-mono text-slate-500 w-10">{hora(v.created_at)}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">#{v.numero} · {v.store_nombre}</p>
              <p className="text-[11px] text-slate-500 truncate">{v.resumen}{v.n_items > 3 ? "…" : ""}</p>
              <p className="mt-1 flex flex-wrap gap-1">
                <span data-testid={`venta-vivo-canal-${v.sale_id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md border border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{CANAL[v.canal] || v.canal || "Caja"}</span>
                <span data-testid={`venta-vivo-doc-${v.sale_id}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md border border-white/10 text-slate-300">{v.doc_nombre}{v.doc_folio ? ` N° ${v.doc_folio}` : ""}</span>
              </p>
            </div>
            <div className="text-right">
              <p className={`font-mono font-bold ${v.estado === "ANULADA" ? "line-through text-rose-400" : ""}`}>{clp(v.total)}</p>
              <p className="text-[10px] text-slate-500">{METODO[v.payment_method] || v.payment_method}</p>
            </div>
          </Glass>
        </Rise>
      ))}
    </div>
  );
};

export default function Ventas() {
  const { me, storeId } = useMobile();
  const [periodo, setPeriodo] = useState("semana");
  const [d, setD] = useState(null);
  useEffect(() => {
    if (me.tier !== "PRO") return;
    setD(null);
    api.get("/mobile/analitica", { params: { periodo, store_id: storeId || undefined } }).then((r) => setD(r.data));
  }, [periodo, storeId, me.tier]);

  if (me.tier !== "PRO") return (
    <div className="pt-5 space-y-3" data-testid="mobile-ventas-locked">
      <h1 className="text-2xl font-bold font-display">Ventas</h1>
      <ProLock testId="ventas-pro-lock-vivo" titulo="Ventas en vivo" detalle="Cada venta de tus cajas, autoservicio y tienda online, al instante." />
      <ProLock testId="ventas-pro-lock-analitica" titulo="Comparativos semana, mes y año" detalle="Gráficos, top 10 productos, utilidad y margen, medios de pago, canales y ranking de vendedores." />
    </div>
  );

  return (
    <div className="pt-5" data-testid="mobile-ventas">
      <h1 className="text-2xl font-bold font-display">Ventas</h1>
      <div className="mt-4 flex gap-1.5 p-1 rounded-full bg-[#0B1324] border border-white/[0.08]" data-testid="ventas-periodo">
        {PERIODOS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriodo(k)} data-testid={`ventas-periodo-${k}`}
                  className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors duration-200 ${periodo === k ? "bg-cyan-500/20 text-cyan-300" : "text-slate-400"}`}>{l}</button>
        ))}
      </div>
      {!d ? <Spinner /> : (<>
        <Rise className="mt-4"><Glass className="p-5 border-cyan-500/25" data-testid="ventas-total">
          <p className="text-[11px] uppercase tracking-[0.16em] text-cyan-400/90 font-semibold">Total del período</p>
          <p className="mt-1 text-3xl font-extrabold font-mono tracking-tight">{clp(d.total)}</p>
          <p className="mt-1 text-xs text-slate-400"><span className="font-mono">{d.tickets}</span> tickets · promedio <span className="font-mono">{clp(d.promedio)}</span>
            {d.vs_anterior != null && <span className={`ml-2 font-mono ${d.vs_anterior >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{d.vs_anterior > 0 ? "+" : ""}{d.vs_anterior}% vs período anterior</span>}</p>
          <div className="h-32 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.serie}>
                <XAxis dataKey="k" tick={{ fill: "#475569", fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(34,211,238,.06)" }} contentStyle={{ background: "#0B1324", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, fontSize: 12 }} formatter={(v) => clp(v)} />
                <Bar dataKey="total" fill="#22D3EE" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Glass></Rise>
        <Rise i={1} className="mt-3 grid grid-cols-2 gap-2">
          <Glass className="p-4" data-testid="ventas-utilidad"><TrendingUp className="w-4 h-4 text-emerald-400" />
            <p className="mt-2 font-mono text-lg font-bold">{clp(d.utilidad.utilidad)}</p><p className="text-[11px] text-slate-400">Utilidad estimada (neto − costo)</p></Glass>
          <Glass className="p-4" data-testid="ventas-margen"><Layers className="w-4 h-4 text-cyan-400" />
            <p className={`mt-2 font-mono text-lg font-bold ${d.utilidad.margen_pct < 0 ? "text-rose-400" : ""}`}>{d.utilidad.margen_pct ?? "—"}%</p><p className="text-[11px] text-slate-400">Margen sobre venta neta</p></Glass>
        </Rise>
        <SectionTitle right={<Trophy className="w-4 h-4 text-amber-400" />}>Top productos</SectionTitle>
        <Rank testId="ventas-top-productos" rows={d.top_productos} label={(r) => r.nombre} value={(r) => `${clp(r.total)} · ${Number(r.cantidad).toLocaleString("es-CL")} u`} />
        <SectionTitle right={<CreditCard className="w-4 h-4 text-slate-500" />}>Medios de pago</SectionTitle>
        <Rank testId="ventas-medios" rows={d.medios_pago} label={(r) => METODO[r.metodo] || r.metodo} />
        <SectionTitle>Canales</SectionTitle>
        <Rank testId="ventas-canales" rows={d.canales} label={(r) => CANAL[r.canal] || r.canal} />
        <SectionTitle right={<FileText className="w-4 h-4 text-slate-500" />}>Tipo de documento</SectionTitle>
        <Rank testId="ventas-tipos-documento" rows={(d.tipos_documento || []).map((t) => ({ ...t, total: Math.abs(t.total), neg: t.total < 0 }))}
              label={(r) => `${r.nombre} · ${r.cantidad}`} value={(r) => `${r.neg ? "-" : ""}${clp(r.total)}`} />
        <SectionTitle right={<Users className="w-4 h-4 text-slate-500" />}>Vendedores</SectionTitle>
        <Rank testId="ventas-vendedores" rows={d.vendedores} label={(r) => `${r.nombre} · ${r.tickets} tickets`} />
      </>)}
      <SectionTitle right={<span className="flex items-center gap-1 text-[10px] text-emerald-400 font-bold"><Radio className="w-3 h-3 animate-pulse" /> EN VIVO</span>}>Últimas ventas</SectionTitle>
      <Vivo storeId={storeId} />
    </div>
  );
}
